<div class="analytics-filter-container">
  <!-- Filter Header -->
  <div class="filter-header">
    <div class="d-flex justify-content-between align-items-center">
      <div class="filter-title"></div>

      <!-- Action Buttons -->
      <div class="filter-actions">
        @if (filterConfig().showResetButton) {
          <button
            kendoButton
            themeColor="secondary"
            size="small"
            class="me-2"
            (click)="onResetFiltersHandler()"
            [disabled]="!hasActiveFilters()"
          >
            Reset
          </button>
        }

        @if (filterConfig().showApplyButton) {
          <button
            kendoButton
            class="cyflare-btn-secondary cyflare-btn-sm"
            (click)="onApplyFiltersHandler()"
            [svgIcon]="filterIcon"
          >
            Apply
          </button>
        }
      </div>
    </div>
  </div>

  <!-- Filter Controls -->
  <div class="filter-controls">
    <!-- Time Range Filter -->
    @if (filterConfig().showTimeRange) {
      <div
        class="filter-group time-group"
        style="max-width: 400px"
      >
        <div class="filter-label">Time Range</div>
        <kendo-dropdownlist
          [data]="timeRanges()"
          [value]="selectedTimeRange()"
          (valueChange)="onTimeRangeChangeHandler($event)"
          [textField]="'text'"
          [valueField]="'value'"
          placeholder="Select time range"
          class="w-100"
          style="max-width: 400px"
        ></kendo-dropdownlist>

        <!-- Custom Date Range -->
        @if (selectedTimeRange() && selectedTimeRange()?.value === 'custom') {
          <div class="date-range mt-1">
            <kendo-datepicker
              [value]="startDate()"
              (valueChange)="onStartDateChangeHandler($event)"
              placeholder="Start date"
              class="me-1"
            ></kendo-datepicker>
            <span class="date-separator">to</span>
            <kendo-datepicker
              [value]="endDate()"
              (valueChange)="onEndDateChangeHandler($event)"
              placeholder="End date"
              class="ms-1"
            ></kendo-datepicker>
          </div>
        }
      </div>
    }

    <!-- Customer Filters -->
    @if (filterConfig().showCustomerFilters) {
      <div class="filter-group customer-group">
        <div class="filter-label">Customer Filters</div>

        <!-- Industry Filter -->
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="text-uppercase text-muted small fw-bold">Industry</div>
          <div class="btn-group btn-group-sm">
            <button
              type="button"
              kendoButton
              class="cyflare-btn-secondary cyflare-btn-sm"
              (click)="selectAllIndustriesHandler()"
            >
              Select All
            </button>
            <button
              type="button"
              kendoButton
              class="cyflare-btn-secondary cyflare-btn-sm"
              (click)="clearIndustriesHandler()"
            >
              Clear
            </button>
          </div>
        </div>
        <kendo-multiselect
          [data]="filteredIndustries()"
          [value]="selectedIndustries()"
          (valueChange)="onIndustryChangeHandler($event)"
          [textField]="'display_text'"
          [valueField]="'id'"
          [filterable]="true"
          (filterChange)="onIndustryFilterChangeHandler($event)"
          [kendoMultiSelectSummaryTag]="selectedIndustries().length > 4 ? 0 : selectedIndustries().length"
          placeholder="Select industries"
          class="w-100 mb-1"
        >
          <ng-template
            kendoMultiSelectGroupTagTemplate
            let-dataItems
          >
            {{ dataItems.length }} industries selected
          </ng-template>
        </kendo-multiselect>

        <!-- Organization Filter -->
        @if (orgService()) {
          <p-treeSelect
            [(ngModel)]="selectedOrganizationsModel"
            [options]="organizationTreeData()"
            display="chip"
            [metaKeySelection]="false"
            selectionMode="checkbox"
            placeholder="Select organizations"
            [filter]="true"
            [propagateSelectionDown]="true"
            [propagateSelectionUp]="false"
            [loading]="isLoadingOrganizations()"
            [appendTo]="'body'"
            (onShow)="onOrganizationDropdownOpened($event)"
            (onNodeSelect)="onOrganizationNodeChange()"
            (onNodeUnselect)="onOrganizationNodeChange()"
          >
            <ng-template pTemplate="value">
              @if (getSelectedOrganizationCount() > 0) {
                <div class="d-flex flex-wrap gap-1 align-items-center">
                  @for (node of getVisibleSelectedNodes(); track node.key) {
                    <span class="p-treeselect-chip">
                      {{ node.label }}
                      <span
                        class="p-treeselect-chip-icon pi pi-times"
                        (click)="removeNode($event, node)"
                      ></span>
                    </span>
                  }
                  @if (getSelectedOrganizationCount() > 1) {
                    <span class="p-treeselect-chip summary-chip">+{{ getSelectedOrganizationCount() - 1 }} more</span>
                  }
                </div>
              } @else {
                <span class="p-placeholder">Select organizations</span>
              }
            </ng-template>
          </p-treeSelect>
        } @else {
          <div class="text-muted small">Organization service not available</div>
        }
      </div>
    }

    <!-- Service Filters -->
    @if (filterConfig().showServiceFilters) {
      <div class="filter-group service-group">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="filter-label mb-0">Service Group</div>
          <div class="btn-group btn-group-sm">
            <button
              type="button"
              kendoButton
              class="cyflare-btn-secondary cyflare-btn-sm"
              (click)="selectAllServiceGroupsHandler()"
            >
              Select All
            </button>
            <button
              type="button"
              kendoButton
              class="cyflare-btn-secondary cyflare-btn-sm"
              (click)="clearServiceGroupsHandler()"
            >
              Clear
            </button>
          </div>
        </div>
        <kendo-multiselect
          [data]="groupedServices()"
          [value]="selectedServiceGroups()"
          (valueChange)="onServiceGroupChangeHandler($event)"
          [textField]="'group'"
          [valueField]="'group'"
          [valuePrimitive]="true"
          [checkboxes]="true"
          [kendoMultiSelectSummaryTag]="selectedServiceGroups().length > 4 ? 0 : selectedServiceGroups().length"
          placeholder="Select service group"
          class="w-100"
        >
          <ng-template
            kendoMultiSelectGroupTagTemplate
            let-dataItems
          >
            {{ dataItems.length }} service groups selected
          </ng-template>
        </kendo-multiselect>
      </div>
    }

    <!-- Tool Filters -->
    @if (filterConfig().showToolFilters) {
      <div class="filter-group tools-group">
        <div class="filter-label">Security Tools</div>
        <kendo-multiselect
          [data]="filteredProductsByService()"
          [textField]="'name'"
          [valueField]="'id'"
          [valuePrimitive]="false"
          [checkboxes]="true"
          [filterable]="true"
          [value]="selectedTools()"
          (valueChange)="onToolsChangeHandler($event)"
          [placeholder]="'Filter by tools...'"
          class="w-100"
        ></kendo-multiselect>
      </div>
    }

    <!-- Custom Filters -->
    @if (filterConfig().showCustomFilters) {
      <div class="filter-group custom-filters">
        <div class="filter-label">Custom Filters</div>
        <ng-content select="[custom-filters]"></ng-content>
      </div>
    }
  </div>
</div>
